import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/queryClient";
import { adminUpdateUser } from "@/lib/callables";
import type { AppUser, Coupon, Order, Subscription, UserAddress } from "@/types";
import { fetchAggregate, fetchCollection, fetchDocument, orderBy, where } from "./use-firestore";
import { usePagedQuery } from "./use-paged-query";

export function useClients() {
  return useQuery({
    queryKey: queryKeys.users(),
    queryFn: () => fetchCollection<AppUser>("users"),
    select: (rows) =>
      [...rows].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "pt-BR")),
  });
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.user(id ?? ""),
    queryFn: () => fetchDocument<AppUser>("users", id as string),
    enabled: Boolean(id),
  });
}

/**
 * Os endereços vivem em dois lugares: um array no documento do usuário e a
 * subcoleção `users/{id}/addresses`. As duas convivem desde antes deste painel,
 * e a tela mostra a subcoleção, que é a forma mais recente.
 */
export function useClientAddresses(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.userAddresses(id ?? ""),
    queryFn: () => fetchCollection<UserAddress>(`users/${id}/addresses`),
    enabled: Boolean(id),
  });
}

/** Os pedidos do cliente, uma página por vez, do mais recente para o mais antigo. */
export function useClientOrders(id: string | undefined) {
  const total = useQuery({
    queryKey: [...queryKeys.userOrders(id ?? ""), "count"],
    queryFn: () =>
      fetchAggregate("orders", [where("userId", "==", id as string), orderBy("createdAt", "desc")]),
    enabled: Boolean(id),
    select: (result) => result.count,
  });
  return usePagedQuery<Order>({
    key: queryKeys.userOrders(id ?? ""),
    path: "orders",
    constraints: [where("userId", "==", id ?? ""), orderBy("createdAt", "desc")],
    enabled: Boolean(id),
    total: total.data,
  });
}

/**
 * Pedidos entregues e total gasto em toda a vida da conta, por agregação. Só
 * filtros de igualdade (`userId` e `status`), atendidos pelo índice que já
 * existe para essa dupla.
 */
export function useClientSpending(id: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.userOrders(id ?? ""), "spending"],
    queryFn: () =>
      fetchAggregate(
        "orders",
        [where("userId", "==", id as string), where("status", "==", "delivered")],
        "total",
      ),
    enabled: Boolean(id),
  });
}

export function useClientCoupons(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.userCoupons(id ?? ""),
    queryFn: () =>
      fetchCollection<Coupon>("coupons", where("userIds", "array-contains", id as string)),
    enabled: Boolean(id),
  });
}

export function useClientSubscriptions(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.userSubscriptions(id ?? ""),
    queryFn: () =>
      fetchCollection<Subscription>("subscriptions", where("userId", "==", id as string)),
    enabled: Boolean(id),
  });
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; data: Record<string, unknown> }) =>
      adminUpdateUser(input),
    onSuccess: (_result, input) => {
      qc.invalidateQueries({ queryKey: queryKeys.user(input.userId) });
      qc.invalidateQueries({ queryKey: queryKeys.users() });
    },
  });
}
